import { concatStyleSets, IButtonStyles, IStyle } from '@fluentui/react';

import { IH2OTheme } from '../../models/components.models';
import { FontSizes, FontWeights, LineHeights, Sizes, Transitions } from '../../themes/styles';

const getIconLike = (theme: IH2OTheme): Partial<IButtonStyles> => ({
  root: {
    color: theme.semanticColors?.textSecondary,
  },
  label: {
    fontWeight: 600,
  },
  rootHovered: {
    color: theme.semanticColors?.buttonText,
  },
  icon: {
    color: theme.semanticColors?.textSecondary,
  },
});
export interface IButtonContainerStyles {
  root: IStyle;
  loader?: IStyle;
  loaderContainer?: IStyle;
}

export interface IButtonContainerClassNames {
  root: string;
  loader?: string;
  loaderContainer?: string;
}

const menuButtonWidthAndDividerWidth = '39px';

export const buttonContainerStylesDefault: IButtonContainerStyles = {
  root: {
    position: 'relative',
    display: 'inline-block',
    lineHeight: 0,
    '&.primary-disabled > div > span > span': {
      display: 'none',
    },
    '&.loader-with-menu-button': {
      overflow: 'hidden',
      '.h2o-ButtonContainer-loaderContainer': {
        marginLeft: `-${menuButtonWidthAndDividerWidth}`,
        paddingLeft: menuButtonWidthAndDividerWidth,
        '.ms-Spinner': {
          borderRadius: '4px 0 0 4px',
        },
      },
    },
  },
  loaderContainer: {
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
  },
  loader: {
    pointerEvents: 'none',
    position: 'relative',
    height: '100%',
  },
};

export const buttonContainerStylesLink: IButtonContainerStyles = {
  root: {
    width: 'fit-content',
  },
};

const coreButtonLabelStyleObject: IStyle = {
  margin: 0,
  textAlign: 'left',
  fontWeight: FontWeights.medium,
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  lineHeight: Sizes.height - 2,
  transition: `all ${Transitions.short}`,
  color: 'inherit',
};

export const buttonLabelStylesDefault = (): IButtonStyles => ({
  root: coreButtonLabelStyleObject,
});

export const buttonStylesDefault = (theme: IH2OTheme): IButtonStyles => ({
  root: {
    border: `1px solid ${theme.semanticColors?.buttonBorder}`,
    borderRadius: Sizes.borderRadius,
    color: theme.semanticColors?.buttonText,
    minHeight: Sizes.height,
    minWidth: 0,
    height: Sizes.height,
    lineHeight: Sizes.height,
    padding: `${Sizes.buttonPaddingTopBottom}px ${Sizes.padding - 1}px`,
    fontSize: FontSizes.button,
    transition: `all ${Transitions.short}`,
    backgroundColor: 'rgba(0,0,0,0)',
  },
  rootHovered: {
    backgroundColor: theme.semanticColors?.buttonHoverBackground,
    borderColor: theme.semanticColors?.buttonHoverBorder,
    color: theme.semanticColors?.buttonHoverText,
  },
  rootFocused: {
    borderColor: theme.semanticColors?.buttonActiveBorder,
    color: theme.semanticColors?.buttonText,
    backgroundColor: theme.semanticColors?.buttonActiveBackground,
  },
  rootPressed: {
    borderColor: theme.semanticColors?.buttonActiveBorder,
    color: theme.semanticColors?.buttonText,
    backgroundColor: theme.semanticColors?.buttonActiveBackground,
  },
  rootDisabled: {
    backgroundColor: theme.semanticColors?.buttonDisabledBackground,
    borderColor: theme.semanticColors?.buttonDisabledBorderColor,
    color: theme.semanticColors?.textDisabled,
  },
  rootExpanded: {
    borderColor: theme.semanticColors?.buttonActiveBorder,
    color: theme.semanticColors?.buttonText,
    backgroundColor: theme.semanticColors?.buttonActiveBackground,
  },
  label: coreButtonLabelStyleObject,
  secondaryText: {
    color: theme.semanticColors?.buttonDefaultSecondaryText,
    fontSize: FontSizes.buttonSecondary,
    transition: `all ${Transitions.short}`,
  },
  icon: {
    color: theme.semanticColors?.buttonIconColor,
    fontSize: FontSizes.buttonIcon,
    transition: `all ${Transitions.short}`,
    marginLeft: 0,
    marginRight: 8,
  },
  menuIcon: {
    color: theme.semanticColors?.inputReadOnlyText,
    fontSize: FontSizes.optionIcon,
    transition: `all ${Transitions.short}`,
    margin: '0 0 0 8px',
  },
  textContainer: { overflow: 'hidden', whiteSpace: 'nowrap' },
});

export const buttonStylesPrimary = (theme: IH2OTheme): IButtonStyles => ({
  root: {
    color: theme.semanticColors?.buttonPrimaryText,
    backgroundColor: theme.semanticColors?.buttonPrimaryBackground,
    borderColor: theme.semanticColors?.buttonPrimaryBackground,
  },
  rootFocused: {
    backgroundColor: theme.semanticColors?.buttonPrimaryActiveBackground,
    borderColor: theme.semanticColors?.buttonPrimaryActiveBorder,
    color: theme.semanticColors?.buttonPrimaryText,
  },
  rootPressed: {
    backgroundColor: theme.semanticColors?.buttonPrimaryActiveBackground,
    borderColor: theme.semanticColors?.buttonPrimaryActiveBorder,
    color: theme.semanticColors?.buttonPrimaryText,
  },
  rootHovered: {
    backgroundColor: theme.semanticColors?.buttonPrimaryHoverBackground,
    borderColor: theme.semanticColors?.buttonPrimaryHoverBackground,
    color: theme.semanticColors?.buttonPrimaryText,
  },
  icon: {
    color: theme.semanticColors?.buttonPrimaryText,
  },
  menuIcon: {
    color: theme.semanticColors?.buttonPrimaryText,
  },
  rootDisabled: {
    color: theme.semanticColors?.buttonPrimaryDisabledText,
    backgroundColor: theme.semanticColors?.buttonPrimaryDisabledBackground,
    borderColor: theme.semanticColors?.buttonPrimaryDisabledBorder,
    pointerEvents: 'none',
  },
});

export const buttonStylesSecondary = (theme: IH2OTheme): IButtonStyles => ({
  root: {
    color: theme.semanticColors?.buttonSecondaryText,
    backgroundColor: theme.semanticColors?.buttonSecondaryBackground,
    borderColor: theme.semanticColors?.buttonSecondaryBackground,
  },
  rootFocused: {
    backgroundColor: theme.semanticColors?.buttonSecondaryActiveBackground,
    borderColor: theme.semanticColors?.buttonSecondaryActiveBorder,
    color: theme.semanticColors?.buttonSecondaryText,
  },
  rootPressed: {
    backgroundColor: theme.semanticColors?.buttonSecondaryActiveBackground,
    borderColor: theme.semanticColors?.buttonSecondaryActiveBorder,
    color: theme.semanticColors?.buttonSecondaryText,
  },
  rootHovered: {
    backgroundColor: theme.semanticColors?.buttonSecondaryHoverBackground,
    borderColor: theme.semanticColors?.buttonSecondaryHoverBackground,
    color: theme.semanticColors?.buttonSecondaryText,
  },
  icon: {
    color: theme.semanticColors?.buttonSecondaryText,
  },
  menuIcon: {
    color: theme.semanticColors?.buttonSecondaryText,
  },
  rootDisabled: {
    color: theme.semanticColors?.buttonSecondaryDisabledText,
    backgroundColor: theme.semanticColors?.buttonSecondaryDisabledBackground,
    borderColor: theme.semanticColors?.buttonSecondaryDisabledBorder,
    pointerEvents: 'none',
  },
});

export const buttonStylesGhost = (theme: IH2OTheme): IButtonStyles => ({
  root: {
    color: theme.semanticColors?.buttonGhostText,
    backgroundColor: theme.semanticColors?.buttonGhostBackground,
    borderColor: theme.semanticColors?.buttonGhostBorder,
  },
  rootFocused: {
    backgroundColor: theme.semanticColors?.buttonGhostBackgroundFocused,
    borderColor: theme.semanticColors?.buttonGhostBorderFocused,
    color: theme.semanticColors?.buttonGhostTextFocused,
  },
  rootPressed: {
    backgroundColor: theme.semanticColors?.buttonGhostBackgroundFocused,
    borderColor: theme.semanticColors?.buttonGhostBorderFocused,
    color: theme.semanticColors?.buttonGhostTextFocused,
  },
  rootHovered: {
    backgroundColor: theme.semanticColors?.buttonGhostBackgroundHovered,
    borderColor: theme.semanticColors?.buttonGhostBorderHovered,
    color: theme.semanticColors?.buttonGhostTextHovered,
  },
  icon: {
    color: theme.semanticColors?.buttonGhostText,
  },
  menuIcon: {
    color: theme.semanticColors?.buttonGhostText,
  },
  rootDisabled: {
    color: theme.semanticColors?.buttonGhostTextDisabled,
    backgroundColor: theme.semanticColors?.buttonGhostBackgroundDisabled,
    borderColor: theme.semanticColors?.buttonGhostBorderDisabled,
    pointerEvents: 'none',
  },
});

export const buttonStylesDanger = (theme: IH2OTheme): IButtonStyles => ({
  root: {
    color: theme.palette?.white,
    backgroundColor: theme.semanticColors?.buttonDanger,
    borderColor: theme.semanticColors?.buttonDanger,
  },
  rootHovered: {
    color: theme.palette?.white,
    backgroundColor: theme.semanticColors?.buttonDangerHovered,
    borderColor: theme.semanticColors?.buttonDangerHovered,
  },
  rootFocused: {
    color: theme.palette?.white,
    backgroundColor: theme.semanticColors?.buttonDangerHovered,
    borderColor: theme.semanticColors?.buttonDangerActiveBorder,
  },
  rootPressed: {
    color: theme.palette?.white,
    backgroundColor: theme.semanticColors?.buttonDangerHovered,
    borderColor: theme.semanticColors?.buttonDangerActiveBorder,
  },
  icon: {
    color: theme.palette?.white,
  },
  iconPressed: {
    color: theme.semanticColors?.buttonText,
  },
  menuIcon: {
    color: theme.palette?.white,
  },
  rootDisabled: {
    color: theme.palette?.white,
    backgroundColor: theme.semanticColors?.buttonDangerDisabled,
    borderColor: theme.semanticColors?.buttonDangerDisabled,
    pointerEvents: 'none',
  },
});

export const buttonStylesStealth = (theme: IH2OTheme): IButtonStyles => ({
  root: {
    color: theme.semanticColors?.buttonStealthText,
    backgroundColor: theme.semanticColors?.buttonStealthBackground,
    borderColor: theme.semanticColors?.buttonStealthBackground,
    padding: `${Sizes.buttonPaddingTopBottom}px ${Sizes.buttonPaddingLeftRightSmall}px`,
  },
  rootFocused: {
    backgroundColor: theme.semanticColors?.buttonStealthActiveBackground,
    borderColor: theme.semanticColors?.buttonStealthActiveBorder,
    color: theme.semanticColors?.buttonStealthText,
  },
  rootPressed: {
    backgroundColor: theme.semanticColors?.buttonStealthActiveBackground,
    borderColor: theme.semanticColors?.buttonStealthActiveBorder,
    color: theme.semanticColors?.buttonStealthText,
  },
  rootHovered: {
    backgroundColor: theme.semanticColors?.buttonStealthHoverBackground,
    borderColor: theme.semanticColors?.buttonStealthHoverBackground,
    color: theme.semanticColors?.buttonStealthText,
  },
  icon: {
    color: theme.semanticColors?.buttonStealthText,
  },
  menuIcon: {
    color: theme.semanticColors?.buttonStealthText,
  },
  label: {
    fontWeight: FontWeights.semiBold,
  },
  rootDisabled: {
    color: theme.semanticColors?.textDisabled,
    backgroundColor: theme.semanticColors?.buttonStealthDisabledBackground,
    borderColor: theme.semanticColors?.buttonStealthDisabledBackground,
    pointerEvents: 'none',
  },
});

export const buttonStylesStealthPageTitle = (theme: IH2OTheme): IButtonStyles =>
  concatStyleSets(buttonStylesStealth(theme), {
    root: {
      padding: 0,
      height: 24,
      minHeight: 24,
    },
    icon: {
      fontSize: FontSizes.xsmall,
      width: 14,
      color: theme.semanticColors?.textPrimary,
    },
    label: {
      fontSize: FontSizes.large,
      fontWeight: FontWeights.normal,
      color: theme.semanticColors?.textPrimary,
    },
  });

export const buttonStylesDefaultWidth: IButtonStyles = {
  root: {
    width: Sizes.buttonWidth,
  },
  label: {
    textAlign: 'center',
  },
};

export const buttonStylesWidthSmall: IButtonStyles = {
  root: {
    width: Sizes.buttonWidthSmall,
  },
  label: {
    textAlign: 'center',
  },
};

export const buttonStylesWidthXSmall: IButtonStyles = {
  root: {
    width: 112,
  },
  label: {
    textAlign: 'center',
  },
};

export const buttonStylesLarge: IButtonStyles = {
  root: {
    height: Sizes.buttonHeightLarge,
    minHeight: Sizes.buttonHeightLarge,
  },
};

export const buttonStylesSmall: IButtonStyles = {
  root: {
    height: Sizes.buttonHeightSmall,
    minHeight: Sizes.buttonHeightSmall,
    padding: `0 ${Sizes.buttonPaddingLeftRightSmall}px`,
  },
};

export const buttonStylesXSmall: IButtonStyles = {
  root: {
    height: Sizes.buttonHeightXSmall,
    minHeight: Sizes.buttonHeightXSmall,
    padding: `0 ${Sizes.buttonPaddingLeftRightSmall}px`,
  },
  label: {
    fontSize: FontSizes.xxsmall,
    fontWeight: FontWeights.normal,
    lineHeight: Sizes.buttonHeightXSmall - 2,
  },
};

export const buttonStylesCenter: IButtonStyles = {
  label: {
    textAlign: 'center',
  },
};

export const buttonStylesSplit = (theme: IH2OTheme): IButtonStyles => ({
  splitButtonContainer: {
    borderRadius: Sizes.borderRadius,
    overflow: 'hidden',
    transition: `all ${Transitions.short}`,
    border: `solid 1px ${theme.semanticColors?.buttonPrimaryBackground}`,
    boxSizing: 'border-box',
    height: Sizes.height,
  },
  splitButtonContainerHovered: {
    borderColor: theme.semanticColors?.buttonPrimaryBackground,
  },
  splitButtonContainerFocused: {
    borderColor: theme.semanticColors?.inputActiveBorder,
  },
  splitButtonContainerDisabled: {
    border: `1px solid ${theme.semanticColors?.inputDisabledNoBorder}`,
  },
  root: {
    border: 0, // split button has different structure. It has 2 buttons(button and menu button) and has a container element for them. root is for the left button.
    borderRadius: 0,
    backgroundColor: theme.semanticColors?.buttonPrimaryBackground,
    width: Sizes.buttonSplitLeftWidth,
    height: Sizes.height - 2,
    lineHeight: Sizes.height - 2,
    minHeight: Sizes.height - 2,
    color: theme.semanticColors?.buttonPrimaryText,
    padding: `${Sizes.buttonPaddingTopBottom}px ${Sizes.padding}px`,
  },
  rootHovered: {
    backgroundColor: theme.semanticColors?.buttonPrimaryHoverBackground,
    color: theme.semanticColors?.buttonPrimaryText,
  },
  splitButtonMenuButton: {
    transition: `all ${Transitions.short}`,
    border: 0,
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    backgroundColor: theme.semanticColors?.buttonPrimaryBackground,
    color: theme.semanticColors?.buttonPrimaryText,
    width: Sizes.buttonSplitRightWidth,
    '&:hover': {
      border: 0,
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
      backgroundColor: theme.semanticColors?.buttonPrimaryHoverBackground,
      color: theme.semanticColors?.buttonPrimaryText,
    },
    '&:hover .ms-Button-icon': {
      color: `${theme.semanticColors?.buttonPrimaryText} !important`,
    },
  },
  splitButtonMenuIcon: {
    color: theme.semanticColors?.buttonPrimaryText,
    fontSize: FontSizes.caretDown,
    margin: 0,
  },
  splitButtonMenuButtonExpanded: {
    border: 0,
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    borderColor: theme.semanticColors?.buttonPrimaryHoverBackground,
    backgroundColor: theme.semanticColors?.buttonPrimaryHoverBackground,
    '&:hover': {
      borderColor: theme.semanticColors?.buttonPrimaryHoverBackground,
      backgroundColor: theme.semanticColors?.buttonPrimaryHoverBackground,
      color: theme.semanticColors?.buttonPrimaryText,
    },
  },
  splitButtonDivider: {
    backgroundColor: theme.semanticColors?.buttonDividerBackground,
    right: Sizes.buttonSplitRightWidth - 1,
  },
  icon: {
    color: theme.semanticColors?.buttonPrimaryText,
  },
});

export const buttonStylesSplitPrimary = buttonStylesSplit;

export const buttonStylesSplitSecondary = (theme: IH2OTheme): IButtonStyles =>
  buttonStylesSplit({
    ...theme,
    semanticColors: {
      ...theme.semanticColors,
      buttonPrimaryBackground: theme.semanticColors?.buttonSecondaryBackground,
      buttonPrimaryHoverBackground: theme.semanticColors?.buttonSecondaryHoverBackground,
      buttonPrimaryText: theme.semanticColors?.buttonSecondaryText,
      inputActiveBorder: theme.semanticColors?.buttonSecondaryActiveBorder,
    },
  });

export const buttonStylesSplitGhost = (theme: IH2OTheme): IButtonStyles =>
  concatStyleSets(
    buttonStylesSplit({
      ...theme,
      semanticColors: {
        ...theme.semanticColors,
        buttonPrimaryBackground: theme.semanticColors?.buttonGhostBackground,
        buttonPrimaryHoverBackground: theme.semanticColors?.buttonGhostBackgroundHovered,
        buttonPrimaryText: theme.semanticColors?.buttonGhostText,
        inputActiveBorder: theme.semanticColors?.buttonGhostBorderFocused,
        buttonDividerBackground: theme.semanticColors?.buttonGhostBorder,
      },
    }),
    {
      splitButtonContainer: {
        borderColor: theme.semanticColors?.buttonGhostBorder,
      },
      splitButtonContainerHovered: {
        borderColor: theme.semanticColors?.buttonGhostBorderHovered,
      },
      splitButtonContainerFocused: {
        borderColor: theme.semanticColors?.buttonGhostBorderFocused,
      },
    }
  );

export const buttonStylesSplitDanger = (theme: IH2OTheme): IButtonStyles =>
  buttonStylesSplit({
    ...theme,
    semanticColors: {
      ...theme.semanticColors,
      buttonPrimaryBackground: theme.semanticColors?.buttonDanger,
      buttonPrimaryHoverBackground: theme.semanticColors?.buttonDangerHovered,
      buttonPrimaryText: theme.palette?.white,
      inputActiveBorder: theme.semanticColors?.buttonDangerActiveBorder,
      buttonDividerBackground: theme.palette?.white,
    },
  });

export const buttonStylesSplitSmall: IButtonStyles = {
  root: {
    width: Sizes.buttonSplitLeftWidthSmall,
    padding: `${Sizes.buttonPaddingTopBottom}px ${Sizes.buttonPaddingLeftRightSmall}px ${Sizes.buttonPaddingTopBottom}px ${Sizes.padding}px`,
  },
};

export const buttonStylesIcon: IButtonStyles = {
  root: {
    border: 0,
    padding: 0,
    minWidth: 0,
    minHeight: '',
    height: 20,
  },
  rootHovered: {
    backgroundColor: 'transparent',
  },
  rootPressed: {
    backgroundColor: 'transparent',
  },
  rootFocused: {
    backgroundColor: 'transparent',
  },
};

export const buttonStylesIcon20: IButtonStyles = {
  icon: {
    fontSize: FontSizes.large,
    margin: 0,
  },
};

export const buttonStylesMenuIcon: IButtonStyles = {
  icon: {
    fontSize: FontSizes.xlarge,
    margin: 0,
  },
};

export const buttonStylesTag: IButtonStyles = {
  root: {
    height: 20,
  },
  icon: {
    fontSize: FontSizes.xxsmall,
  },
};

export const buttonStylesIconColumn = (theme: IH2OTheme): IButtonStyles => ({
  root: {
    backgroundColor: 'transparent',
  },
  icon: {
    color: theme.semanticColors?.buttonIconColumn,
  },
  iconHovered: {
    color: theme.semanticColors?.buttonIconColumnHovered,
  },
});

export const buttonStylesIconLike = (theme: IH2OTheme): IButtonStyles => ({
  ...getIconLike(theme),
});

export const buttonStylesIconLiked = (theme: IH2OTheme): IButtonStyles => ({
  ...getIconLike(theme),
  icon: {
    color: theme.semanticColors?.buttonDanger,
  },
});

export const buttonStylesIconPin = (theme: IH2OTheme): IButtonStyles => ({
  icon: {
    color: theme.semanticColors?.textSecondary,
  },
});

export const buttonStylesIconPinned = (theme: IH2OTheme): IButtonStyles => ({
  icon: {
    color: theme.semanticColors?.buttonDanger,
  },
});

const iconStyle = (theme: IH2OTheme): IStyle => ({
  padding: 0,
  margin: 0,
  color: theme.semanticColors?.textQuinary,
  fontSize: FontSizes.small,
  transition: 'color 0.3s ease-in-out',
  lineHeight: Sizes.height,
  height: Sizes.height,
  ':hover': {
    color: theme.semanticColors?.buttonHoverText,
  },
});

export const buttonStylesIconCard = (theme: IH2OTheme): IButtonStyles => ({
  root: {
    backgroundColor: 'transparent',
    fontSize: FontSizes.small,
    height: Sizes.height,
  },
  rootDisabled: {
    backgroundColor: 'transparent',
  },
  menuIcon: iconStyle(theme),
  icon: iconStyle(theme),
  iconDisabled: {
    color: theme.semanticColors?.textDisabled,
    cursor: 'normal',
    ':hover': {
      color: theme.semanticColors?.textDisabled,
    },
  },
});
export const buttonStylesIconCardLiked = (theme: IH2OTheme): IButtonStyles => ({
  icon: {
    color: theme.semanticColors?.liked,
  },
});

export const buttonStylesIconLarge = (theme: IH2OTheme): IButtonStyles => ({
  root: {
    height: 40,
  },
  icon: {
    fontSize: FontSizes.buttonLargeIcon,
    color: theme.semanticColors?.buttonPrimaryBackground,
  },
});
export const buttonStylesIconCarousel = (theme: IH2OTheme): IButtonStyles => ({
  root: {
    height: 35,
    width: 35,
    borderRadius: 999,
    backgroundColor: theme.semanticColors?.buttonCarouselBackground,
  },
  rootHovered: {
    backgroundColor: theme.semanticColors?.buttonCarouselBackgroundHovered,
  },
  rootPressed: {
    backgroundColor: theme.semanticColors?.buttonCarouselBackgroundHovered,
  },
  icon: {
    fontSize: FontSizes.xxsmall,
    color: theme.semanticColors?.buttonCarouselIcon,
    fontWeight: `${FontWeights.bold} !important`,
    margin: 0,
  },
});
export const buttonStylesIconCarouselRight: IButtonStyles = {
  icon: {
    marginRight: -2,
  },
};
export const buttonStylesIconCarouselLeft: IButtonStyles = {
  icon: {
    marginLeft: -2,
  },
};
export const buttonStylesMenu = (theme: IH2OTheme): IButtonStyles => ({
  root: {
    border: '0',
    minHeight: 40,
    width: 'auto',
    maxWidth: 400,
    height: 'auto',
  },
  rootHovered: {
    backgroundColor: theme.semanticColors?.buttonStealthHoverBackground,
  },
  rootPressed: {
    backgroundColor: theme.semanticColors?.buttonStealthActiveBackground,
  },
  rootFocused: {
    backgroundColor: theme.semanticColors?.buttonStealthActiveBackground,
  },
  rootExpanded: {
    backgroundColor: theme.semanticColors?.buttonStealthHoverBackground,
  },
  flexContainer: {
    alignItems: 'flex-start',
  },
  menuIcon: {
    color: theme.semanticColors?.buttonPrimaryText,
    marginTop: 2,
    marginLeft: 16,
    fontSize: FontSizes.buttonCompound,
  },
});
export const buttonStylesLogin = (theme: IH2OTheme): IButtonStyles => ({
  root: {
    padding: '16px 12px',
    height: 72,
    color: theme.semanticColors?.textQuaternary,
    borderColor: theme.semanticColors?.buttonPrimaryBackground,
    backgroundColor: theme.semanticColors?.buttonPrimaryBackground,
  },
  rootHovered: {
    color: theme.semanticColors?.textQuaternary,
    borderColor: theme.semanticColors?.buttonPrimaryHoverBackground,
    backgroundColor: theme.semanticColors?.buttonPrimaryHoverBackground,
  },
  rootFocused: {
    color: theme.semanticColors?.textQuaternary,
    borderColor: theme.semanticColors?.buttonPrimaryActiveBorder,
    backgroundColor: theme.semanticColors?.buttonPrimaryActiveBackground,
  },
  rootPressed: {
    color: theme.semanticColors?.textQuaternary,
    borderColor: theme.semanticColors?.buttonPrimaryActiveBorder,
    backgroundColor: theme.semanticColors?.buttonPrimaryActiveBackground,
  },
  icon: {
    color: theme.palette?.primary900,
    fontSize: 28,
    marginTop: 4,
  },
  label: {
    textAlign: 'left',
    fontSize: FontSizes.button,
    fontWeight: FontWeights.semiBold,
    lineHeight: LineHeights.xsmall,
    margin: '0 0 2px 0',
  },
  description: {
    color: theme.palette?.primary900,
  },
  descriptionHovered: {
    color: theme.palette?.primary900,
  },
  descriptionPressed: {
    color: theme.palette?.primary900,
  },
});

export const buttonStylesLink = (theme: IH2OTheme): IButtonStyles => ({
  root: {
    width: 'fit-content',
    borderColor: 'transparent',
    backgroundColor: 'transparent',
    color: theme.semanticColors?.buttonLinkText,
    fontSize: FontSizes.xsmall,
    padding: 0,
    'span span span': {
      lineHeight: LineHeights.normal,
    },
  },
  rootDisabled: {
    background: 'transparent',
  },
  rootHovered: {
    borderColor: 'transparent',
    backgroundColor: 'transparent',
    color: theme.semanticColors?.buttonLinkHoverText,
  },
  rootFocused: {
    borderColor: 'transparent',
    backgroundColor: 'transparent',
    color: theme.semanticColors?.buttonLinkHoverText,
  },
  rootPressed: {
    borderColor: 'transparent',
    backgroundColor: 'transparent',
    color: theme.semanticColors?.buttonLinkHoverText,
  },
  icon: {
    color: theme.semanticColors?.buttonLinkText,
    fontSize: FontSizes.xsmall,
    height: FontSizes.xsmall,
    '&:focus': {
      color: theme.semanticColors?.buttonLinkHoverText,
    },
  },
  iconHovered: {
    color: theme.semanticColors?.buttonLinkHoverText,
  },
  menuIcon: {
    fontSize: FontSizes.xxxsmall,
    color: theme.semanticColors?.buttonLinkIcon,
    '&:focus': {
      color: theme.semanticColors?.buttonLinkHoverText,
    },
  },
  menuIconHovered: {
    color: theme.semanticColors?.buttonLinkHoverText,
  },
});

export const buttonStylesLinkHighlighted: IButtonStyles = {
  label: {
    fontWeight: FontWeights.semiBold,
  },
};

export const buttonStylesLinkLarge: IButtonStyles = {
  root: {
    fontSize: FontSizes.small,
  },
  icon: {
    fontSize: FontSizes.large,
  },
  label: {
    fontWeight: FontWeights.normal,
  },
};

export const buttonStylesLinkUnderline = (theme: IH2OTheme): IButtonStyles => ({
  root: {
    color: theme.semanticColors?.buttonLinkUnderlineText,
    fontSize: FontSizes.xsmall,
    fontWeight: FontWeights.normal,
  },
  rootHovered: {
    color: theme.semanticColors?.buttonLinkUnderlineHoverText,
  },
  rootFocused: {
    color: theme.semanticColors?.buttonLinkUnderlineHoverText,
  },
  rootPressed: {
    color: theme.semanticColors?.buttonLinkUnderlineHoverText,
  },
  icon: {
    color: theme.semanticColors?.buttonLinkUnderlineText,
    '&:focus': {
      color: theme.semanticColors?.buttonLinkUnderlineHoverText,
    },
  },
  iconHovered: {
    color: theme.semanticColors?.buttonLinkUnderlineHoverText,
  },
  menuIcon: {
    color: theme.semanticColors?.buttonLinkUnderlineText,
    '&:focus': {
      color: theme.semanticColors?.buttonLinkUnderlineHoverText,
    },
  },
  menuIconHovered: {
    color: theme.semanticColors?.buttonLinkUnderlineHoverText,
  },
  label: {
    color: theme.semanticColors?.buttonLinkUnderlineText,
    textDecoration: 'underline',
    fontSize: FontSizes.xsmall,
    fontWeight: FontWeights.normal,
  },
});

export const buttonStylesLinkBlack = (theme: IH2OTheme): IButtonStyles => ({
  root: {
    color: theme.semanticColors?.buttonPrimaryText,
  },
  icon: {
    color: theme.semanticColors?.buttonPrimaryText,
  },
  menuIcon: {
    color: theme.semanticColors?.buttonPrimaryText,
  },
  label: {
    fontWeight: FontWeights.normal,
  },
});

export const buttonStylesUnderline = {
  root: { textDecoration: 'underline' },
};

export const buttonStylesLog = (theme: IH2OTheme): IButtonStyles => ({
  root: {
    fontSize: FontSizes.xsmall,
    color: theme.semanticColors?.buttonDefaultSecondaryText,
    paddingRight: 16,
  },
  rootDisabled: {
    backgroundColor: theme.palette?.white,
    color: theme.semanticColors?.textDisabled,
    outline: 'transparent',
    borderStyle: 'none',
  },
});

export const buttonStylesInlineRight = (theme: IH2OTheme): IButtonStyles => ({
  root: {
    border: `1px solid ${theme.semanticColors?.buttonInlineBorder}`,
    borderRadius: Sizes.borderRadius,
    color: theme.semanticColors?.buttonPrimaryText,
    cursor: 'pointer',
    fontSize: FontSizes.button,
    backgroundColor: theme.semanticColors?.buttonInlineBackground,
    borderBottomLeftRadius: 0,
    borderTopLeftRadius: 0,
    borderBottomRightRadius: Sizes.borderRadius,
    borderTopRightRadius: Sizes.borderRadius,
    minHeight: Sizes.height,
    height: Sizes.height,
    width: Sizes.height,
    maxWidth: Sizes.height,
    minWidth: Sizes.height,
  },
  icon: {
    color: theme.semanticColors?.buttonPrimaryText,
    fontSize: FontSizes.xsmall,
    margin: 0,
  },
  rootHovered: {
    color: theme.semanticColors?.buttonInlineHoverText,
    backgroundColor: theme.semanticColors?.buttonInlineHoverBackground,
    border: `1px solid ${theme.semanticColors?.buttonInlineBorder}`,
  },
  rootFocused: {
    border: `1px solid ${theme.semanticColors?.buttonInlineBorder}`,
  },
  rootPressed: {
    border: `1px solid ${theme.semanticColors?.buttonInlineBorder}`,
  },
  rootDisabled: {
    border: `1px solid ${theme.semanticColors?.buttonInlineBorder}`,
  },
  iconHovered: {
    color: theme.semanticColors?.buttonInlineHoverText,
  },
});

export const buttonStylesInlineRightSuccess = (theme: IH2OTheme): IButtonStyles => ({
  root: {
    color: theme.semanticColors?.buttonInlineSuccess,
    opacity: 1,
    height: Sizes.height,
    width: Sizes.height,
    maxWidth: Sizes.height,
    minWidth: Sizes.height,
    backgroundColor: theme.semanticColors?.buttonInlineHoverBackground,
  },
  icon: {
    color: theme.semanticColors?.buttonInlineSuccess,
    opacity: 1,
    transition: 'none',
  },
  iconHovered: {
    color: theme.semanticColors?.buttonInlineSuccess,
    transition: 'none',
  },
});

export const buttonStylesIconInlineBlock = (theme: IH2OTheme): IButtonStyles => ({
  root: {
    border: 'none',
    color: theme.semanticColors?.buttonPrimaryText,
    cursor: 'pointer',
    maxHeight: 22,
    minHeight: 22,
    minWidth: 22,
    maxWidth: 22,
    top: 2,
    left: 5,
    backgroundColor: 'transparent',
  },
  rootHovered: {
    color: theme.semanticColors?.buttonHoverText,
    backgroundColor: 'transparent',
  },
  rootPressed: {
    backgroundColor: 'transparent',
    color: theme.palette?.black,
  },
  rootFocused: {
    backgroundColor: 'transparent',
  },
  icon: {
    color: theme.semanticColors?.buttonPrimaryText,
    fontSize: FontSizes.xsmall,
    backgroundColor: 'transparent',
    margin: 0,
  },
  iconHovered: {
    backgroundColor: 'transparent',
  },
});

export const buttonStylesIconInlineBlockSuccess = (theme: IH2OTheme): IButtonStyles => ({
  root: {
    color: theme.semanticColors?.buttonInlineSuccess,
  },
  icon: {
    color: theme.semanticColors?.buttonInlineSuccess,
    transition: 'none',
  },
  iconHovered: {
    color: theme.semanticColors?.buttonInlineSuccess,
    transition: 'none',
  },
  rootHovered: {
    color: theme.semanticColors?.buttonInlineHoverText,
    backgroundColor: 'transparent',
  },
});

export const buttonStylesTextWithIconRight = {
  icon: {
    margin: '0 0 0 8px',
  },
  flexContainer: {
    flexDirection: 'row-reverse',
  },
};
